import { useQuery } from '@tanstack/react-query';
import { useCurrentLocale } from '@/hooks/useCurrentLocale';
import { logger } from '@/utils/logger';
import { resolveToPrimarySync } from '@/hooks/usePrimaryLocale';

export interface LocalizedLocation {
  id: string;
  iso: string | null;
  name: string;
  short: string | null;
  native: string;
  english: string;
  slug: string | null;
  search: string;
}

interface BundledLocation {
  id: string;
  iso: string | null;
  slug: string | null;
  english: string;
  short: string | null;
  native: string;
  names: Record<string, string>;
}

// Cache the bundled data to avoid refetching
let cachedLocations: BundledLocation[] | null = null;

/**
 * Fetches bundled location data from static JSON and maps to current locale.
 */
async function fetchLocalizedLocations(locale: string): Promise<LocalizedLocation[]> {
  try {
    // Fetch bundled data if not cached
    if (!cachedLocations) {
      const response = await fetch('/data/locations.json');
      if (!response.ok) {
        logger.error('Failed to load locations.json');
        return [];
      }
      cachedLocations = await response.json();
    }

    // Resolve to primary locale (e.g., 'pt-BR' → 'pt', 'zh-HK' → 'zh-TW')
    // Note: JSON uses 36 primary language codes, resolveToPrimarySync handles special cases
    const primaryLocale = resolveToPrimarySync([], locale).toLowerCase();

    // Map to LocalizedLocation with current locale's name
    const mapped = cachedLocations!.map((loc): LocalizedLocation => {
      // Use primary locale key directly (JSON uses 36 primary language codes)
      const translatedName = loc.names[primaryLocale] || loc.names['en'] || loc.english;

      // Include short name in search terms for better matching
      const searchTerms = [loc.short, ...Object.values(loc.names)].filter(Boolean).join(' ');

      return {
        id: String(loc.id),
        iso: loc.iso,
        name: translatedName,
        short: loc.short,
        native: loc.native,
        english: loc.english,
        slug: loc.slug,
        search: searchTerms,
      };
    });

    // Browser locale preferences can contain POSIX suffixes such as
    // "en-US@posix", which Intl rejects as a language tag.
    const sortLocale = locale.split('@')[0]?.replace('_', '-') || 'en';
    let collator: Intl.Collator;
    try {
      collator = new Intl.Collator(sortLocale);
    } catch {
      collator = new Intl.Collator('en');
    }

    return mapped.sort((a, b) => collator.compare(a.name, b.name));
  } catch (error) {
    logger.error('Error loading locations', { error });
    return [];
  }
}

/**
 * Hook to fetch location data with user-friendly display names.
 * 
 * @description
 * This is the PRIMARY hook for USER-FACING UI that displays location names.
 * Uses React Query for caching and loads from static JSON for performance.
 * Prioritizes `name` (translated) > `native` > `english` for display.
 * 
 * **Data Sources:**
 * - Static JSON file at `/data/locations.json` (bundled with all translations)
 * 
 * **When to use:**
 * - Location dropdowns and selectors in user-facing UI
 * - Any component showing location names to end users
 * - Location badges and labels
 * 
 * **When NOT to use:**
 * - Admin components that need technical data → use `useLocations`
 * - URL slug generation → use the `slug` field
 * 
 * @returns {{ locations: LocalizedLocation[], isLoading: boolean, error: Error | null }}
 * 
 * @example
 * const { locations, isLoading } = useLocalizedLocations();
 * // Returns: [{ id: "1", name: "Espagne", native: "España", english: "Spain", ... }]
 */
interface UseLocalizedLocationsOptions {
  /** Sort by English name instead of translated name (for admin use) */
  sortByEnglish?: boolean;
}

export function useLocalizedLocations(options: UseLocalizedLocationsOptions = {}) {
  const { sortByEnglish = false } = options;
  const locale = useCurrentLocale();

  const { data: locations = [], isLoading, error } = useQuery({
    queryKey: ['localized-locations', locale],
    queryFn: () => fetchLocalizedLocations(locale),
    staleTime: Infinity,  // Static file - never refetch during session
    gcTime: 60 * 60 * 1000,  // Keep in memory for 1 hour
    // No enabled guard needed - i18n.language is immediately available
    select: (data) => {
      if (sortByEnglish) {
        // Re-sort by English name for admin
        return [...data].sort((a, b) => a.english.localeCompare(b.english, 'en'));
      }
      return data;
    },
  });

  return { locations, isLoading, error };
}
